import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { getCountAsync } from "../../../slices/inventory/count";
import { getStorages } from "../../../slices/inventory/storage";
import { getCountItemsAsync, deleteCountAsync } from "../../../slices/inventory/count_item";
import { addItemToCount, updateItemInCount, removeItemFromCount, approveCountItem, bulkApproveCountItems } from "../../../api/inventory/count_items";
import { approveCount as approveCountAPI } from "../../../api/inventory/count";
import { ResourceEmpty } from "../../../components/utils/ResourceEmpty";
import {
  ArrowLeftIcon,
  BuildingOffice2Icon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Button, TextField, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { axiosWithToast } from "../../../components/Utils";
import { CountAddProductDialog } from "../../../components/dashboard/inventory/counts/CountAddProductDialog";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useSettings } from "../../../contexts/settings-context";
import axios from "../../../api/axios";
import { updateCount } from "../../../api/inventory/count";

// Helper function to format numbers with Norwegian locale (1 000,00)
const formatNumber = (value) => {
  return (value || 0).toLocaleString("nb-NO", { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
};

// Helper function to format quantity (10,00)
const formatQuantity = (value) => {
  return (value || 0).toLocaleString("nb-NO", { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
};

export const InventoryCount = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { priceFormatter } = useSettings();
  
  const itemState = useSelector((store) => store.inventory_counts);
  const { isLoading, error, errors, count } = itemState;
  const countItemsState = useSelector((store) => store.inventory_count_items);
  // Sort items by id (or created_at if available) to maintain consistent order
  const countItems = useMemo(() => {
    const items = Array.isArray(countItemsState?.data) ? countItemsState.data : [];
    // Sort by id (ascending) to maintain consistent order - older items first
    return [...items].sort((a, b) => {
      // First try created_at if available
      if (a.created_at && b.created_at) {
        return new Date(a.created_at) - new Date(b.created_at);
      }
      // Fallback to id
      return (a.id || 0) - (b.id || 0);
    });
  }, [countItemsState?.data]);

  const storagesState = useSelector((store) => store.inventory_storages);
  const storages = storagesState?.data || [];
  
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [updatingItem, setUpdatingItem] = useState(null);
  const [localItems, setLocalItems] = useState([]);
  const [duplicateConfirmDialog, setDuplicateConfirmDialog] = useState(null);
  const [approvingItem, setApprovingItem] = useState(null);
  const [approvingCount, setApprovingCount] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [countDate, setCountDate] = useState("");
  const [updatingDate, setUpdatingDate] = useState(false);
  const [editingStorage, setEditingStorage] = useState(false);
  const [selectedStorageId, setSelectedStorageId] = useState("");
  const [updatingStorage, setUpdatingStorage] = useState(false);
  const [exporting, setExporting] = useState(false);

  const item = count;
  const selectedStorage = storages.find((s) => s.id === item?.storage_id);

  useEffect(() => {
    if (id) {
      dispatch(getCountAsync(id));
      dispatch(getCountItemsAsync(id));
    }
    if (storages.length === 0) {
      dispatch(getStorages());
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (item?.date) {
      setCountDate(item.date);
    }
  }, [item?.date]);

  useEffect(() => {
    if (item?.storage_id) {
      setSelectedStorageId(item.storage_id);
    }
  }, [item?.storage_id]);

  useEffect(() => {
    // Only update localItems if countItems actually changed
    // Use length and IDs to avoid deep comparison issues
    const countItemsIds = countItems.map(item => item.id).join(',');
    const localItemsIds = localItems.map(item => item.id).join(',');
    
    if (countItems.length !== localItems.length || countItemsIds !== localItemsIds) {
      setLocalItems([...countItems]);
    }
  }, [countItems]);

  const handleAddItem = async (product, formData, skipDuplicateCheck = false) => {
    if (!product) {
      toast.error(t("inventory.count.product.select_required"));
      return;
    }

    // Check if product already exists in the list
    if (!skipDuplicateCheck) {
      const existingItem = countItems.find((item) => item.product_id === product.id);
      if (existingItem) {
        // Show confirmation dialog
        setDuplicateConfirmDialog({
          product: product,
          existingItem: existingItem,
          formData: formData,
        });
        return;
      }
    }

    try {
      const response = await addItemToCount(id, {
        product_id: product.id,
        quantity: formData?.quantity || 1,
        purchase_price: product.purchase_price || 0,
        note: formData?.note || "",
      });

      if (response?.success) {
        dispatch(getCountItemsAsync(id));
        toast.success(t("inventory.count.item.added"));
        setDuplicateConfirmDialog(null);
      } else {
        const errorMessage = response?.errors?.join(", ") || t("inventory.count.item.add_error");
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error adding item:", error);
      const errorMessage = error.response?.data?.errors?.join(", ") || t("inventory.count.item.add_error");
      toast.error(errorMessage);
    }
  };

  const handleConfirmDuplicate = async () => {
    if (duplicateConfirmDialog) {
      await handleAddItem(
        duplicateConfirmDialog.product,
        duplicateConfirmDialog.formData,
        true // Skip duplicate check
      );
    }
  };

  const handleUpdateItem = async (item) => {
    if (!item.quantity || item.quantity <= 0) {
      toast.error(t("inventory.count.quantity_required"));
      return;
    }

    setUpdatingItem(item.id);
    try {
      const response = await updateItemInCount(id, item.id, {
        quantity: item.quantity,
        purchase_price: item.purchase_price || 0,
        note: item.note || "",
      });

      if (response?.success) {
        dispatch(getCountItemsAsync(id));
        setEditingItem(null);
        setUpdatingItem(null);
        toast.success(t("inventory.count.item.updated"));
      } else {
        const errorMessage = response?.errors?.join(", ") || t("inventory.count.item.update_error");
        toast.error(errorMessage);
        setUpdatingItem(null);
      }
    } catch (error) {
      console.error("Error updating item:", error);
      const errorMessage = error.response?.data?.errors?.join(", ") || t("inventory.count.item.update_error");
      toast.error(errorMessage);
      setUpdatingItem(null);
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!confirm(t("inventory.count.item.delete_confirm"))) return;

    try {
      const response = await removeItemFromCount(id, itemId);
      if (response?.success) {
        dispatch(getCountItemsAsync(id));
        toast.success(t("inventory.count.item.deleted"));
      } else {
        const errorMessage = response?.errors?.join(", ") || t("inventory.count.item.delete_error");
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error removing item:", error);
      const errorMessage = error.response?.data?.errors?.join(", ") || t("inventory.count.item.delete_error");
      toast.error(errorMessage);
    }
  };

  const handleApproveItem = async (itemId, approved) => {
    setApprovingItem(itemId);
    try {
      const response = await approveCountItem(id, itemId, approved);
      if (response?.success) {
        dispatch(getCountItemsAsync(id));
        toast.success(approved ? t("inventory.count.item.approved") : t("inventory.count.item.unapproved"));
      } else {
        const errorMessage = response?.errors?.join(", ") || t("inventory.count.item.approve_error");
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error approving item:", error);
      const errorMessage = error.response?.data?.errors?.join(", ") || t("inventory.count.item.approve_error");
      toast.error(errorMessage);
    } finally {
      setApprovingItem(null);
    }
  };

  const handleApproveAllItems = async () => {
    const unapprovedItems = countItems.filter(item => !item.approved);
    if (unapprovedItems.length === 0) {
      toast.info(t("inventory.count.all_items_already_approved"));
      return;
    }

    setApprovingCount(true);
    try {
      // Use bulk approve instead of individual requests
      const itemIds = unapprovedItems.map(item => item.id);
      const response = await bulkApproveCountItems(id, itemIds, true);
      
      if (response?.success) {
        dispatch(getCountItemsAsync(id));
        toast.success(t("inventory.count.approve_all_success", { count: response.updated_count || itemIds.length }));
      } else {
        const errorMessage = response?.errors?.join(", ") || t("inventory.count.approve_all_error");
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error approving all items:", error);
      const errorMessage = error.response?.data?.errors?.join(", ") || t("inventory.count.approve_all_error");
      toast.error(errorMessage);
    } finally {
      setApprovingCount(false);
    }
  };

  const handleApproveCount = async (approved) => {
    setApprovingCount(true);
    try {
      const response = await approveCountAPI(id, approved);
      if (response?.id || response?.success !== false) {
        dispatch(getCountAsync(id));
        dispatch(getCountItemsAsync(id));
        toast.success(approved ? t("inventory.count.approved") : t("inventory.count.unapproved"));
      } else {
        const errorMessage = response?.errors?.join(", ") || t("inventory.count.approve_error");
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error approving count:", error);
      const errorMessage = error.response?.data?.errors?.join(", ") || t("inventory.count.approve_error");
      toast.error(errorMessage);
    } finally {
      setApprovingCount(false);
    }
  };

  const handleUpdateDate = async () => {
    if (!countDate) {
      toast.error(t("inventory.count.date_required"));
      return;
    }
    setUpdatingDate(true);
    try {
      const response = await updateCount({
        id: id,
        date: countDate,
      });
      if (response?.id) {
        dispatch(getCountAsync(id));
        setEditingDate(false);
        toast.success(t("inventory.count.date_updated"));
      } else {
        const errorMessage = response?.errors?.join(", ") || t("inventory.count.date_update_error");
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error updating date:", error);
      const errorMessage = error.response?.data?.errors?.join(", ") || t("inventory.count.date_update_error");
      toast.error(errorMessage);
    } finally {
      setUpdatingDate(false);
    }
  };

  const handleUpdateStorage = async () => {
    setUpdatingStorage(true);
    try {
      const response = await updateCount({
        id: id,
        storage_id: selectedStorageId || null,
      });
      if (response?.id) {
        dispatch(getCountAsync(id));
        setEditingStorage(false);
        toast.success(t("inventory.count.storage_updated"));
      } else {
        const errorMessage = response?.errors?.join(", ") || t("inventory.count.storage_update_error");
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error updating storage:", error);
      const errorMessage = error.response?.data?.errors?.join(", ") || t("inventory.count.storage_update_error");
      toast.error(errorMessage);
    } finally {
      setUpdatingStorage(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`/inventory/counts/${id}/export_pdf`, {
        responseType: 'blob',
      });
      
      // Check if response is actually a PDF blob
      if (response.data instanceof Blob) {
        // Verify it's a PDF by checking the first bytes
        const clonedBlob = response.data.slice(0);
        const arrayBuffer = await clonedBlob.slice(0, 4).arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const firstBytes = String.fromCharCode(...uint8Array);
        
        if (!firstBytes.startsWith('%PDF')) {
          // Not a PDF, might be JSON error - read the whole blob
          const text = await clonedBlob.text();
          try {
            const json = JSON.parse(text);
            throw new Error(json.errors?.join(', ') || json.error || t("inventory.count.export_pdf_error"));
          } catch (e) {
            if (e.message.includes('errors') || e.message.includes('error')) {
              throw e;
            }
            throw new Error(t("inventory.count.export_pdf_error"));
          }
        }
        
        // Create download link
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `varetelling_${item?.count_number || id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(t("inventory.count.export_pdf_success"));
      } else {
        toast.error(t("inventory.count.export_pdf_error"));
      }
    } catch (error) {
      console.error("Error exporting PDF:", error);
      // If error response has data, try to parse as JSON
      if (error.response && error.response.data) {
        if (error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const json = JSON.parse(text);
            toast.error(json.errors?.join(', ') || json.error || t("inventory.count.export_pdf_error"));
          } catch (e) {
            toast.error(t("inventory.count.export_pdf_error"));
          }
        } else {
          toast.error(error.response?.data?.error || t("inventory.count.export_pdf_error"));
        }
      } else {
        toast.error(t("inventory.count.export_pdf_error"));
      }
    } finally {
      setExporting(false);
    }
  };

  const handleExportCSV = () => {
    try {
      const headers = [
        t("inventory.count.product"),
        t("inventory.count.item_number"),
        t("inventory.count.quantity"),
        t("inventory.count.purchase_price"),
        t("inventory.count.total"),
        t("inventory.count.note"),
        t("inventory.count.approved"),
      ];
      
      const rows = countItems.map((item) => [
        item.product?.displayTitle || item.product?.title || "",
        item.product?.item_number || "",
        formatQuantity(item.quantity || 0),
        formatNumber(item.purchase_price || 0),
        formatNumber((item.quantity || 0) * (item.purchase_price || 0)),
        item.note || "",
        item.approved ? t("Yes") : t("No"),
      ]);

      const csvContent = [
        headers.join(";"),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(";"))
      ].join("\n");

      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `varetelling_${item?.count_number || id}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(t("inventory.count.export_csv_success"));
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error(t("inventory.count.export_csv_error"));
    }
  };

  const headerMeta = [
    {
      label: item?.count_number || "",
      hidden: !item?.count_number,
    },
    {
      label: selectedStorage ? selectedStorage.title || selectedStorage.storage_number : "",
      icon: BuildingOffice2Icon,
      hidden: !selectedStorage,
    },
  ];

  const headerActions = [];

    if (isLoading) {
    return (
      <>
        <DashboardHeader title={t("inventory.count.title")} subtitle={t("Loading...")} />
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <ResourceLoading />
        </div>
      </>
    );
  }

    if (error && errors) {
    return (
      <>
        <DashboardHeader title={t("inventory.count.title")} subtitle="" />
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <ResourceError errors={errors} />
        </div>
      </>
    );
    }

    if (!item?.id) {
    return (
      <>
        <DashboardHeader title={t("inventory.count.title")} subtitle="" />
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <ResourceEmpty />
        </div>
      </>
    );
  }

  const totalQuantity = Array.isArray(countItems) 
    ? countItems.reduce((sum, item) => sum + (item.quantity || 0), 0)
    : 0;
  const totalValue = Array.isArray(countItems)
    ? countItems.reduce((sum, item) => sum + ((item.quantity || 0) * (item.purchase_price || 0)), 0)
    : 0;
  
  const approvedItemsCount = Array.isArray(countItems)
    ? countItems.filter(item => item.approved).length
    : 0;
  const allItemsApproved = countItems.length > 0 && approvedItemsCount === countItems.length;

  return (
    <>
    <DashboardHeader
        title={item?.title || t("inventory.count.title")}
      subtitle={item?.description || ""}
      meta={headerMeta}
      actions={headerActions}
        backButton={{
              label: t("Back"),
              icon: ArrowLeftIcon,
          path: "/dashboard/inventory/counts",
        }}
      />
      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 pb-24">
        <div className="mt-8 space-y-6">
          {/* Count Info */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
              <div>
                <dt className="text-sm font-medium text-gray-500">{t("inventory.count.count_number")}</dt>
                <dd className="mt-1 text-sm text-gray-900">{item?.count_number || "-"}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t("inventory.count.date")}</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {editingDate ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={countDate}
                        onChange={(e) => setCountDate(e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                        disabled={updatingDate}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleUpdateDate}
                        disabled={updatingDate}
                      >
                        {updatingDate ? t("Saving...") : t("Save")}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setEditingDate(false);
                          setCountDate(item?.date || "");
                        }}
                        disabled={updatingDate}
                      >
                        {t("Cancel")}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span>{item?.date ? new Date(item.date).toLocaleDateString("nb-NO") : "-"}</span>
                      {!item?.approved && (
                        <button
                          onClick={() => setEditingDate(true)}
                          className="text-blue-600 hover:text-blue-800"
                          title={t("Edit")}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t("inventory.count.storage")}</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {editingStorage ? (
                    <div className="flex items-center gap-2">
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>{t("inventory.count.select_storage")}</InputLabel>
                        <Select
                          value={selectedStorageId || ""}
                          label={t("inventory.count.select_storage")}
                          onChange={(e) => setSelectedStorageId(e.target.value)}
                          disabled={updatingStorage}
                        >
                          <MenuItem value="">
                            <em>{t("inventory.count.no_storage")}</em>
                          </MenuItem>
                          {storages.map((storage) => (
                            <MenuItem key={storage.id} value={storage.id}>
                              {storage.title || storage.storage_number || `Storage ${storage.id}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleUpdateStorage}
                        disabled={updatingStorage}
                      >
                        {updatingStorage ? t("Saving...") : t("Save")}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setEditingStorage(false);
                          setSelectedStorageId(item?.storage_id || "");
                        }}
                        disabled={updatingStorage}
                      >
                        {t("Cancel")}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span>{selectedStorage ? (selectedStorage.title || selectedStorage.storage_number) : t("inventory.count.no_storage")}</span>
                      {!item?.approved && (
                        <button
                          onClick={() => setEditingStorage(true)}
                          className="text-blue-600 hover:text-blue-800"
                          title={t("Edit")}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t("inventory.count.total_items")}</dt>
                <dd className="mt-1 text-sm text-gray-900">{countItems.length || 0}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t("inventory.count.total_quantity")}</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatQuantity(totalQuantity)}</dd>
              </div>
            </div>
            
            {/* Approved items overview */}
            {countItems.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <dt className="text-sm font-medium text-gray-500">{t("inventory.count.approved_items")}</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {approvedItemsCount} {t("of")} {countItems.length} {t("inventory.count.items")}
                </dd>
              </div>
            )}

            {item?.description && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <dt className="text-sm font-medium text-gray-500">{t("Description")}</dt>
                <dd className="mt-1 text-sm text-gray-900">{item.description}</dd>
              </div>
            )}
          </div>

          {/* Items Table */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">{t("inventory.count.items")}</h3>
              <div className="flex gap-2">
                {count?.approved ? (
                  <>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<XCircleIcon className="h-5 w-5" />}
                      onClick={() => handleApproveCount(false)}
                      disabled={approvingCount}
                    >
                      {approvingCount ? t("Processing...") : t("inventory.count.unapprove_all")}
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<DocumentTextIcon className="h-5 w-5" />}
                      onClick={handleExportPDF}
                      disabled={exporting}
                    >
                      {exporting ? t("Exporting...") : t("inventory.count.export_pdf")}
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<DocumentArrowDownIcon className="h-5 w-5" />}
                      onClick={handleExportCSV}
                    >
                      {t("inventory.count.export_csv")}
                    </Button>
                  </>
                ) : (
                  <>
                    {countItems.length > 0 && approvedItemsCount < countItems.length && (
                      <Button
                        variant="outlined"
                        color="success"
                        startIcon={<CheckCircleIcon className="h-5 w-5" />}
                        onClick={handleApproveAllItems}
                        disabled={approvingCount}
                      >
                        {approvingCount ? t("Processing...") : t("inventory.count.approve_all")}
                      </Button>
                    )}
                    {allItemsApproved && (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleIcon className="h-5 w-5" />}
                        onClick={() => handleApproveCount(true)}
                        disabled={approvingCount}
                      >
                        {approvingCount ? t("Processing...") : t("inventory.count.approve_count")}
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PlusIcon className="h-5 w-5" />}
                      onClick={() => setAddItemDialogOpen(true)}
                    >
                      {t("inventory.count.add_item")}
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<DocumentTextIcon className="h-5 w-5" />}
                      onClick={handleExportPDF}
                      disabled={exporting}
                    >
                      {exporting ? t("Exporting...") : t("inventory.count.export_pdf")}
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<DocumentArrowDownIcon className="h-5 w-5" />}
                      onClick={handleExportCSV}
                    >
                      {t("inventory.count.export_csv")}
                    </Button>
                  </>
                )}
              </div>
            </div>
            {Array.isArray(countItems) && countItems.length > 0 ? (
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("inventory.count.product")}</TableCell>
                    <TableCell>{t("inventory.count.note")}</TableCell>
                    <TableCell align="right">{t("inventory.count.quantity")}</TableCell>
                    <TableCell align="right">{t("inventory.count.purchase_price")}</TableCell>
                    <TableCell align="right">{t("inventory.count.total")}</TableCell>
                    <TableCell align="center">{t("inventory.count.approved")}</TableCell>
                    <TableCell align="right">{t("Edit")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {countItems.map((item) => (
                    <TableRow key={item.id} hover>
                      {editingItem === item.id ? (
                        <>
                          <TableCell>
                            {item.product?.id ? (
                              <a
                                href={`/dashboard/products/${item.product.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate(`/dashboard/products/${item.product.id}`);
                                }}
                              >
                                {item.product?.displayTitle || item.product?.title || `Produkt ${item.product?.id}`}
                              </a>
                            ) : (
                              item.product?.displayTitle || item.product?.title || `Produkt ${item.product?.id}`
                            )}
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={localItems.find((i) => i.id === item.id)?.note || item.note || ""}
                              onChange={(e) => {
                                const updatedItems = localItems.map((i) =>
                                  i.id === item.id ? { ...i, note: e.target.value } : i
                                );
                                setLocalItems(updatedItems);
                              }}
                              placeholder={t("inventory.count.note_placeholder")}
                              sx={{ minWidth: 200 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              value={localItems.find((i) => i.id === item.id)?.quantity || item.quantity || ""}
                              onChange={(e) => {
                                const updatedItems = localItems.map((i) =>
                                  i.id === item.id ? { ...i, quantity: parseFloat(e.target.value) || 0 } : i
                                );
                                setLocalItems(updatedItems);
                              }}
                              inputProps={{ min: 0.01, step: 0.01 }}
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              value={localItems.find((i) => i.id === item.id)?.purchase_price || item.purchase_price || ""}
                              onChange={(e) => {
                                const updatedItems = localItems.map((i) =>
                                  i.id === item.id ? { ...i, purchase_price: parseFloat(e.target.value) || 0 } : i
                                );
                                setLocalItems(updatedItems);
                              }}
                              inputProps={{ min: 0, step: 0.01 }}
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber((localItems.find((i) => i.id === item.id)?.quantity || item.quantity || 0) * 
                              (localItems.find((i) => i.id === item.id)?.purchase_price || item.purchase_price || 0))}
                          </TableCell>
                          <TableCell align="center">
                            {item.approved ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-600" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-gray-400" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <div className="flex gap-2 justify-end">
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                  const itemToUpdate = localItems.find((i) => i.id === item.id) || item;
                                  handleUpdateItem(itemToUpdate);
                                }}
                                disabled={updatingItem === item.id || item.approved}
                              >
                                {updatingItem === item.id ? t("Saving...") : t("Save")}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  setEditingItem(null);
                                  // Reset localItems to match countItems
                                  setLocalItems([...countItems]);
                                }}
                                disabled={updatingItem === item.id || item.approved}
                              >
                                {t("Cancel")}
                              </Button>
                            </div>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            {item.product?.id ? (
                              <a
                                href={`/dashboard/products/${item.product.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate(`/dashboard/products/${item.product.id}`);
                                }}
                              >
                                {item.product?.displayTitle || item.product?.title || `Produkt ${item.product?.id}`}
                              </a>
                            ) : (
                              item.product?.displayTitle || item.product?.title || `Produkt ${item.product?.id}`
                            )}
                          </TableCell>
                          <TableCell>{item.note || "-"}</TableCell>
                          <TableCell align="right">{formatQuantity(item.quantity || 0)}</TableCell>
                          <TableCell align="right">{formatNumber(item.purchase_price || 0)}</TableCell>
                          <TableCell align="right">
                            {formatNumber((item.quantity || 0) * (item.purchase_price || 0))}
                          </TableCell>
                          <TableCell align="center">
                            {item.approved ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-600" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-gray-400" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <div className="flex gap-2 justify-end">
                              {!item.approved ? (
                                <>
                                  <button
                                    onClick={() => handleApproveItem(item.id, true)}
                                    disabled={approvingItem === item.id}
                                    className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                    title={t("inventory.count.approve")}
                                  >
                                    {approvingItem === item.id ? (
                                      <span className="text-xs">{t("Processing...")}</span>
                                    ) : (
                                      <CheckCircleIcon className="h-4 w-4" />
                                    )}
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingItem(item.id);
                                      // Create a deep copy to avoid reference issues
                                      setLocalItems(countItems.map(item => ({ ...item })));
                                    }}
                                    className="text-blue-600 hover:text-blue-900"
                                    title={t("Edit")}
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleRemoveItem(item.id)}
                                    className="text-red-600 hover:text-red-900"
                                    title={t("delete")}
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => handleApproveItem(item.id, false)}
                                  disabled={approvingItem === item.id}
                                  className="text-orange-600 hover:text-orange-900 disabled:opacity-50"
                                  title={t("inventory.count.unapprove")}
                                >
                                  {approvingItem === item.id ? (
                                    <span className="text-xs">{t("Processing...")}</span>
                                  ) : (
                                    <XCircleIcon className="h-4 w-4" />
                                  )}
                                </button>
                              )}
                            </div>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                  <TableRow className="bg-gray-50 border-t-2 border-gray-300">
                    <TableCell colSpan={2} className="font-semibold text-gray-900 text-base">
                      {t("inventory.count.total")}
                    </TableCell>
                    <TableCell align="right" className="font-semibold text-gray-900 text-base">
                      {formatQuantity(totalQuantity)}
                    </TableCell>
                    <TableCell align="right" className="font-semibold text-gray-900 text-base">
                      {totalQuantity > 0 
                        ? formatNumber(totalValue / totalQuantity)
                        : "-"}
                    </TableCell>
                    <TableCell align="right" className="font-semibold text-lg text-gray-900">
                      {formatNumber(totalValue)}
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <ClipboardDocumentListIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t("inventory.count.no_items")}</h3>
                <p className="text-gray-500">{t("inventory.count.no_items_description")}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Item Dialog */}
      <CountAddProductDialog
        open={addItemDialogOpen}
        onClose={() => setAddItemDialogOpen(false)}
        onProductSelected={handleAddItem}
        onImportComplete={() => {
          // Refresh count items after bulk import
          dispatch(getCountItemsAsync(id));
        }}
        countId={id}
      />

      {/* Duplicate Confirmation Dialog */}
      {duplicateConfirmDialog && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div>
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                  <svg
                    className="h-6 w-6 text-yellow-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                    />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900">
                    {t("inventory.count.duplicate.title")}
                  </h3>
                  <div className="mt-4 text-left">
                    <p className="text-sm text-gray-500 mb-4">
                      {t("inventory.count.duplicate.message")}
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div>
                        <span className="text-sm font-medium text-gray-700">
                          {t("inventory.count.product")}:
                        </span>{" "}
                        <span className="text-sm text-gray-900">
                          {duplicateConfirmDialog.existingItem.product?.displayTitle ||
                            duplicateConfirmDialog.existingItem.product?.title ||
                            `Produkt ${duplicateConfirmDialog.existingItem.product_id}`}
                        </span>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-700">
                          {t("inventory.count.quantity")}:
                        </span>{" "}
                        <span className="text-sm text-gray-900">
                          {duplicateConfirmDialog.existingItem.quantity || 0}
                        </span>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-700">
                          {t("inventory.count.duplicate.added_at")}:
                        </span>{" "}
                        <span className="text-sm text-gray-900">
                          {duplicateConfirmDialog.existingItem.created_at
                            ? new Date(duplicateConfirmDialog.existingItem.created_at).toLocaleString("nb-NO", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "-"}
                        </span>
                      </div>
                      {duplicateConfirmDialog.existingItem.note && (
                        <div>
                          <span className="text-sm font-medium text-gray-700">
                            {t("inventory.count.note")}:
                          </span>{" "}
                          <span className="text-sm text-gray-900">
                            {duplicateConfirmDialog.existingItem.note}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <Button
                  variant="contained"
                  onClick={handleConfirmDuplicate}
                  className="sm:col-start-2"
                >
                  {t("inventory.count.duplicate.confirm")}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setDuplicateConfirmDialog(null)}
                >
                  {t("Cancel")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
